import { Stack, Typography } from '@mui/material';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronRightDoubleIcon } from '@/components/icons/ChevronRightDoubleIcon';
import { ClockRewindIcon } from '@/components/icons/ClockRewindIcon';
import { LinkExternal01Icon } from '@/components/icons/LinkExternal01Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { getHouseholdChatBraintrustLogsUrl } from '@/modules/braintrust/braintrustLogs';
import { useBraintrustLinks } from '@/modules/braintrust/useBraintrustLinks';
import { ChatDisclaimerTooltip } from '@/modules/common/Chat/components/ChatDisclaimerTooltip';
import { CHAT_HEADER_ICON_BUTTON_SX } from '@/modules/common/Chat/components/chatHeaderStyles';
import { COLORS } from '@/styles/tokens/colors';

import { useHouseholdChatContext } from '../contexts/householdChat.context';

export function HouseholdChatHeader() {
  const {
    closeChat,
    conversationId,
    isSingleConversation,
    messages,
    showConversationList,
    startNewConversation,
    view,
  } = useHouseholdChatContext();
  const { showDiagnosticLinks } = useBraintrustLinks();

  const isListView = view === 'list';
  // In conversation view with no messages the user is already on a blank
  // chat, so starting a new one would be a no-op.
  const isOnBlankChat = !isListView && messages.length === 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        borderBottom: `solid 1px ${COLORS.GRAY[200]}`,
        px: 2,
        py: 1,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="title-s">Luminary AI</Typography>
        <ChatDisclaimerTooltip />
      </Stack>
      <Stack direction="row" alignItems="center">
        {/* Internal-only button: keep it left-most, away from the
            customer-facing actions, since it's absent for real users. */}
        {showDiagnosticLinks && !isListView && conversationId && (
          <Tooltip placement="bottom" title="View Braintrust logs">
            <IconButton
              ariaLabel="View Braintrust logs"
              icon={LinkExternal01Icon}
              onClick={() =>
                window.open(
                  getHouseholdChatBraintrustLogsUrl({ conversationId }),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              size="xs"
              sx={CHAT_HEADER_ICON_BUTTON_SX}
              variant="transparent"
            />
          </Tooltip>
        )}
        {!isSingleConversation && (
          <Tooltip placement="bottom" title="New chat">
            <IconButton
              ariaLabel="New chat"
              disabled={isOnBlankChat}
              icon={PlusIcon}
              onClick={startNewConversation}
              size="xs"
              sx={CHAT_HEADER_ICON_BUTTON_SX}
              variant="transparent"
            />
          </Tooltip>
        )}
        {!isListView && !isSingleConversation && (
          <Tooltip placement="bottom" title="Chat history">
            <IconButton
              ariaLabel="Chat history"
              icon={ClockRewindIcon}
              onClick={showConversationList}
              size="xs"
              sx={CHAT_HEADER_ICON_BUTTON_SX}
              variant="transparent"
            />
          </Tooltip>
        )}
        <IconButton
          ariaLabel="Hide chat"
          icon={ChevronRightDoubleIcon}
          onClick={closeChat}
          size="xs"
          sx={CHAT_HEADER_ICON_BUTTON_SX}
          variant="transparent"
        />
      </Stack>
    </Stack>
  );
}
